import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Player } from "../types";
import { BingoSelection } from "../activeRound";

// Bingo Bango Bongo per-hole picker. Same magnified scroll-wheel UX as the
// Oosler picker, but it steps through THREE categories in play order —
// Bingo (first on green) → Bango (closest once all on) → Bongo (first in cup).
// Every hole awards all three, so the wheel lists players only (no "None").
// Fires when the first score on a hole commits; picking the third winner
// completes the hole. An outside tap commits the centred player for the
// current category and advances; Escape cancels the whole hole (re-prompts).

type Cat = "bingo" | "bango" | "bongo";
const CATS: Cat[] = ["bingo", "bango", "bongo"];
const CAT_LABEL: Record<Cat, string> = {
  bingo: "Bingo",
  bango: "Bango",
  bongo: "Bongo",
};

interface Props {
  holeNumber: number;
  players: Player[];
  anchorRect: DOMRect;
  onComplete: (selection: BingoSelection) => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 180;
const PADDING_X = 12;
// Taller header slot — the category name renders large so the Bingo → Bango →
// Bongo change is unmissable.
const LABEL_HEIGHT = 44;

export default function BingoSelectionModal({
  holeNumber,
  players,
  anchorRect,
  onComplete,
  onClose,
}: Props) {
  const items = players.map((p, i) => ({
    value: p.id,
    label: p.name || `Player ${i + 1}`,
  }));

  const [step, setStep] = useState(0);
  const stepRef = useRef(step);
  stepRef.current = step;
  const picksRef = useRef<Partial<Record<Cat, string>>>({});

  const [value, setValue] = useState<string>(items[0]?.value ?? "");
  const valueRef = useRef(value);
  valueRef.current = value;

  const overlayRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(false);

  // Reset the wheel to the top (first player) at the start of each category.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = 0;
    const first = items[0]?.value ?? "";
    setValue(first);
    valueRef.current = first;
    armedRef.current = false;
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, [step]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        const next = items[clamped]?.value;
        if (next !== undefined && next !== valueRef.current) setValue(next);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [items]);

  // Record a winner for the current category; advance, or complete on Bongo.
  const commitPick = useCallback(
    (pid: string) => {
      const cat = CATS[stepRef.current];
      picksRef.current[cat] = pid;
      if (stepRef.current < CATS.length - 1) {
        setStep((s) => s + 1);
      } else {
        const picks = picksRef.current;
        onComplete({
          bingo: picks.bingo ?? pid,
          bango: picks.bango ?? pid,
          bongo: picks.bongo ?? pid,
        });
        onClose();
      }
    },
    [onComplete, onClose],
  );

  const onItemTap = useCallback(
    (pid: string) => {
      valueRef.current = pid;
      setValue(pid);
      commitPick(pid);
    },
    [commitPick],
  );

  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commitPick(valueRef.current);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") commitPick(valueRef.current);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commitPick, onClose]);

  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const pickerW = WHEEL_WIDTH + PADDING_X * 2;
  const pickerH = wheelH + LABEL_HEIGHT + PADDING_X * 2;
  const cellCenterX = anchorRect.left + anchorRect.width / 2;
  const cellCenterY = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cellCenterX - pickerW / 2;
  let top = cellCenterY - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  const cat = CATS[step];

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick"
        role="dialog"
        aria-label={`${CAT_LABEL[cat]} selection for hole ${holeNumber}`}
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div className="cg-score-pick-cols">
          <div className="cg-score-pick-col" style={{ width: WHEEL_WIDTH }}>
            {/* key={cat} remounts on each category so the pop animation replays
                and the colour visibly flips Bingo → Bango → Bongo. */}
            <div key={cat} className={`cg-pick-step cg-pick-step-${cat}`}>
              {CAT_LABEL[cat]}!
            </div>
            <div
              className="cg-score-pick-wheel"
              style={{ height: wheelH, width: WHEEL_WIDTH }}
            >
              <div
                className="cg-score-pick-indicator"
                style={{ top: ITEM_HEIGHT, height: ITEM_HEIGHT }}
              />
              <div
                ref={scrollerRef}
                className="cg-score-pick-scroller"
                style={{ height: wheelH }}
              >
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
                {items.map((it) => (
                  <div
                    key={it.value}
                    className={`cg-score-pick-item${
                      it.value === value ? " cg-score-pick-item-on" : ""
                    }`}
                    style={{ height: ITEM_HEIGHT }}
                    onClick={() => onItemTap(it.value)}
                  >
                    {it.label}
                  </div>
                ))}
                <div style={{ height: ITEM_HEIGHT }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
